/**
 * Party Mood catalog for standard-party creation and the party-screen orb.
 *
 * First selected mood is the primary visual mood. This metadata is display
 * intent only — it is not recommendation, search, or catalog input.
 */

export const PARTY_MOODS = [
  { id: 'dance', name: 'Dance', emoji: '💃', color: '#FF3D9A', colorName: 'Hot Pink' },
  { id: 'high-energy', name: 'High Energy', emoji: '⚡', color: '#FF7A00', colorName: 'Electric Orange' },
  { id: 'chill-vibes', name: 'Chill Vibes', emoji: '😎', color: '#22D3EE', colorName: 'Aqua / Cyan' },
  { id: 'romantic', name: 'Romantic', emoji: '❤️', color: '#F43F5E', colorName: 'Rose Red' },
  { id: 'feel-good', name: 'Feel-Good', emoji: '✨', color: '#FBBF24', colorName: 'Golden Yellow' },
  { id: 'lo-fi', name: 'Lo-fi', emoji: '🎶', color: '#A78BFA', colorName: 'Lavender / Purple' },
  { id: 'retro', name: 'Retro', emoji: '🕰️', color: '#14B8A6', colorName: 'Teal' },
];

export const PARTY_MOOD_BY_ID = Object.fromEntries(
  PARTY_MOODS.map((mood) => [mood.id, mood]),
);

export const DEFAULT_PARTY_MOOD_COLOR = '#22D3EE';

export function formatPartyMoodLabel(mood) {
  if (!mood) return '';
  return `${mood.emoji} ${mood.name}`;
}

export function getPartyMoodById(id) {
  return PARTY_MOOD_BY_ID[id] || null;
}

/** Keep known mood ids in first-seen order. */
export function normalizePartyMoods(ids) {
  if (!Array.isArray(ids)) return [];
  const seen = new Set();
  const next = [];
  ids.forEach((id) => {
    if (typeof id !== 'string' || seen.has(id) || !PARTY_MOOD_BY_ID[id]) return;
    seen.add(id);
    next.push(id);
  });
  return next;
}

export function togglePartyMoodSelection(currentIds, moodId) {
  const selected = normalizePartyMoods(currentIds);
  if (!PARTY_MOOD_BY_ID[moodId]) return selected;
  if (selected.includes(moodId)) {
    return selected.filter((id) => id !== moodId);
  }
  return [...selected, moodId];
}

export function getPrimaryPartyMood(ids) {
  const selected = normalizePartyMoods(ids);
  return selected.length > 0 ? PARTY_MOOD_BY_ID[selected[0]] : null;
}

export function getSecondaryPartyMoods(ids) {
  const selected = normalizePartyMoods(ids);
  return selected.slice(1).map((id) => PARTY_MOOD_BY_ID[id]).filter(Boolean);
}

export function partyMoodColor(ids) {
  return getPrimaryPartyMood(ids)?.color || DEFAULT_PARTY_MOOD_COLOR;
}

/** Selected mood colors in selection order, for the multi-color orb. */
export function partyMoodColors(ids) {
  const selected = normalizePartyMoods(ids);
  if (selected.length === 0) return [DEFAULT_PARTY_MOOD_COLOR];
  return selected.map((id) => PARTY_MOOD_BY_ID[id].color);
}

export function formatPartyMoodOrderLabel(mood, index, total) {
  const base = formatPartyMoodLabel(mood);
  if (!base) return '';
  if (total <= 1) return base;
  return `${base} (${index + 1})`;
}

const ORB_COLOR_POSITIONS = [
  '32% 28%',
  '72% 34%',
  '22% 72%',
  '78% 70%',
  '50% 48%',
  '40% 82%',
  '62% 16%',
];

export function partyMoodOrbPalette(colors) {
  if (Array.isArray(colors) && colors.length) return colors;
  return [DEFAULT_PARTY_MOOD_COLOR];
}

/** Layered radials so each selected color stays visible instead of averaging. */
export function partyMoodOrbBackground(colors) {
  const palette = partyMoodOrbPalette(colors);
  if (palette.length === 1) {
    return `radial-gradient(circle at 50% 50%, ${palette[0]}, transparent 48%)`;
  }
  return palette.map((color, index) => (
    `radial-gradient(circle at ${ORB_COLOR_POSITIONS[index % ORB_COLOR_POSITIONS.length]}, ${color} 0%, transparent 46%)`
  )).join(', ');
}

export function partyMoodOrbGlow(colors) {
  const palette = partyMoodOrbPalette(colors);
  const layers = palette.map((color, index) => (
    `0 0 ${18 + index * 6}px ${color}cc`
  ));
  return `0 8px 32px rgba(0,0,0,0.5), ${layers.join(', ')}`;
}

export function partyMoodOrbPosition(index) {
  return ORB_COLOR_POSITIONS[index % ORB_COLOR_POSITIONS.length];
}
